import { DateRangeSelect, SupplierSelect } from '@components';
import { InventoryDateField } from '@services';
import { L } from '@lib';
import { Input, Segmented, Select } from 'antd';
import React from 'react';

interface IProps {
  dateField: InventoryDateField;
  datePreset?: string;
  customFrom?: string;
  customTo?: string;
  categoryOptions: { label: string; value: string }[];
  onDateFieldChange: (field: InventoryDateField) => void;
  onDatePresetChange: (preset?: string) => void;
  onCustomDateRange: (from?: string, to?: string) => void;
  onNameSearch: (text: string) => void;
  onReferenceSearch: (text: string) => void;
  onPartNumberSearch: (text: string) => void;
  onNominalCodeSearch: (text: string) => void;
  onInvoiceNumberSearch: (text: string) => void;
  onCategoryFilter: (values?: string[]) => void;
  onSupplierFilter: (values?: string[]) => void;
  onHasDocumentFilter: (v?: boolean) => void;
  onLowStockFilter: (v?: boolean) => void;
  onIsInShopFilter: (v?: boolean) => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#8c8c8c',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

const dateFieldOptions: { label: string; value: InventoryDateField }[] = [
  { label: 'Date added', value: 'DateAdded' },
  { label: 'Purchase date', value: 'PurchaseDate' },
];

export const InventoryFilters: React.FC<IProps> = ({
  dateField,
  datePreset,
  customFrom,
  customTo,
  categoryOptions,
  onDateFieldChange,
  onDatePresetChange,
  onCustomDateRange,
  onNameSearch,
  onReferenceSearch,
  onPartNumberSearch,
  onNominalCodeSearch,
  onInvoiceNumberSearch,
  onCategoryFilter,
  onSupplierFilter,
  onHasDocumentFilter,
  onLowStockFilter,
  onIsInShopFilter,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '14px 16px',
        marginBottom: 12,
        background: '#fff',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      <style>{`
        .inv-segmented .ant-segmented-item-selected {
          background-color: #1677ff !important;
        }
        .inv-segmented .ant-segmented-item-selected .ant-segmented-item-label {
          color: #fff !important;
          font-weight: 600;
        }
      `}</style>

      {/* Row 1: Name + Reference + Part # + Date filter */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end' }}>
        <div style={{ minWidth: 160, flex: 1 }}>
          <span style={labelStyle}>{L('Name')}</span>
          <Input allowClear placeholder={L('Item name')} onChange={(e) => onNameSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 140, flex: 1 }}>
          <span style={labelStyle}>{L('Reference')}</span>
          <Input allowClear placeholder={L('Stock code')} onChange={(e) => onReferenceSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 140, flex: 1 }}>
          <span style={labelStyle}>{L('Part #')}</span>
          <Input allowClear placeholder={L('From invoice')} onChange={(e) => onPartNumberSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 140, flex: 1 }}>
          <span style={labelStyle}>{L('Nominal Code')}</span>
          <Input allowClear placeholder={L('Nominal code')} onChange={(e) => onNominalCodeSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 240, flex: 1 }}>
          <span style={labelStyle}>{L('Date filter')}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Select
              style={{ flex: 1, minWidth: 160 }}
              value={dateField}
              options={dateFieldOptions.map((o) => ({ ...o, label: L(o.label) }))}
              onChange={onDateFieldChange}
            />
            <DateRangeSelect
              style={{ flex: 1, minWidth: 160 }}
              value={datePreset}
              customFrom={customFrom}
              customTo={customTo}
              onChange={onDatePresetChange}
              onCustomRange={onCustomDateRange}
            />
          </div>
        </div>
      </div>

      {/* Row 2: Category + Supplier + Invoice # */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end' }}>
        <div style={{ minWidth: 200, flex: 1 }}>
          <span style={labelStyle}>{L('Category')}</span>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder={L('All')}
            style={{ width: '100%' }}
            options={categoryOptions}
            onChange={onCategoryFilter}
          />
        </div>
        <div style={{ minWidth: 200, flex: 1 }}>
          <span style={labelStyle}>{L('Supplier')}</span>
          <SupplierSelect
            mode="multiple"
            maxTagCount="responsive"
            placeholder={L('All')}
            onChange={onSupplierFilter}
          />
        </div>
        <div style={{ minWidth: 180, flex: 1 }}>
          <span style={labelStyle}>{L('Invoice #')}</span>
          <Input
            allowClear
            placeholder={L('Invoice number')}
            onChange={(e) => onInvoiceNumberSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Row 3: Segmented toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end' }}>
        <div>
          <span style={labelStyle}>{L('Regular item')}</span>
          <Segmented
            className="inv-segmented"
            options={[
              { label: L('All'), value: 'all' },
              { label: L('Regular'), value: 'in' },
              { label: L('Not regular'), value: 'out' },
            ]}
            defaultValue="all"
            onChange={(v) =>
              onIsInShopFilter(v === 'in' ? true : v === 'out' ? false : undefined)
            }
          />
        </div>
        <div>
          <span style={labelStyle}>{L('Low stock')}</span>
          <Segmented
            className="inv-segmented"
            options={[
              { label: L('All'), value: 'all' },
              { label: L('Low only'), value: 'low' },
            ]}
            defaultValue="all"
            onChange={(v) => onLowStockFilter(v === 'low' ? true : undefined)}
          />
        </div>
        <div>
          <span style={labelStyle}>{L('Has invoice')}</span>
          <Segmented
            className="inv-segmented"
            options={[
              { label: L('All'), value: 'all' },
              { label: L('Yes'), value: 'yes' },
              { label: L('No'), value: 'no' },
            ]}
            defaultValue="all"
            onChange={(v) =>
              onHasDocumentFilter(v === 'yes' ? true : v === 'no' ? false : undefined)
            }
          />
        </div>
      </div>
    </div>
  );
};
